import React, { useCallback } from 'react';
import {
  FEATURE_ALIGNMENT_CONTROL,
  FEATURE_BORDER_RADIUS_CONTROL,
  FEATURE_DIMENSIONS_CONTROL,
  FEATURE_OPACITY_CONTROL,
  FEATURE_POSITION_CONTROL,
  FEATURE_ROTATION_CONTROL,
} from '../flags';
import { DocumentItem } from '../items/pdf/pdf-item-type';
import {
  AccordionHeader,
  ToolButton,
  ValueInputField,
} from '@crabs-ai/ds-react';
import { ChevronLeft, ChevronRight } from '@crabs-ai/web-icons';
import { AlignmentControls } from './controls/alignment-controls';
import { BorderRadiusControl } from './controls/border-radius-controls';
import { DimensionsControls } from './controls/dimensions-controls';
import { OpacityControls } from './controls/opacity-controls';
import { PositionControl } from './controls/position-control';
import { RotationControl } from './controls/rotation-controls';
import { AnimationControls } from './controls/animation-controls';
import { PageKeyframeControls } from './controls/page-keyframe-controls';
import { EMPTY_ANIMATIONS } from '../items/animation/animation-item-type';
import { useInspectorSections } from './use-inspector-sections';
import { CollapsibleContent } from './collapsible-content';
import { TimingControls } from './controls/timing-controls';
import { changeItem } from '../state/actions/change-item';
import { useAssetFromItem, useWriteContext } from '../utils/use-context';
import { useTranslations } from 'next-intl';

const PDF_DEFAULTS = { page: true, time: true, positionShape: true };

/**
 * pdf/ppt 아이템 인스펙터 — img-inspector 미러 + 페이지 넘김 섹션.
 * 페이지 수는 PdfAsset.numPages, 현재 페이지는 item.pageIndex(0-based, UI 는 1-based).
 */
const PdfInspectorUnmemoized: React.FC<{
  item: DocumentItem;
  collapseAllTrigger?: number;
  expandAllTrigger?: number;
}> = ({ item, collapseAllTrigger = 0, expandAllTrigger = 0 }) => {
  const t = useTranslations();
  const { setState } = useWriteContext();
  const asset = useAssetFromItem(item);
  const numPages = asset.type === 'pdf' ? asset.numPages : 1;

  const { openSections, toggleSection } = useInspectorSections({
    itemId: item.id,
    defaults: PDF_DEFAULTS,
    collapseAllTrigger,
    expandAllTrigger,
  });

  const setPageIndex = useCallback(
    (pageIndex: number) => {
      const clamped = Math.min(Math.max(0, pageIndex), numPages - 1);
      setState({
        update: (state) =>
          changeItem(state, item.id, (i) => {
            if (i.type !== 'pdf' && i.type !== 'ppt') return i;
            if (i.pageIndex === clamped) return i;
            return { ...i, pageIndex: clamped };
          }),
        commitToUndoStack: true,
      });
    },
    [setState, item.id, numPages],
  );

  return (
    <div className="layout-padding-bottom">
      <div className="flex flex-col gap-2xs">
        {/* 페이지 넘김 */}
        <div className="border-b border-sys-style-neutral-light px-4xs pb-2xs">
          <AccordionHeader
            size="sm"
            title={t('Editor.inspector.document.page-title')}
            isOpened={openSections.page}
            onClick={() => toggleSection('page')}
          />
          <CollapsibleContent isOpen={openSections.page}>
            <div className="flex items-center gap-4xs px-md py-4xs">
              <ToolButton
                size="sm"
                state={item.pageIndex > 0 ? 'default' : 'disabled'}
                onClick={() => setPageIndex(item.pageIndex - 1)}
                aria-label={t('Editor.inspector.document.prev-page')}
                isShowTooltip
                tooltipNode={t('Editor.inspector.document.prev-page')}
              >
                <ChevronLeft />
              </ToolButton>
              <ValueInputField
                value={item.pageIndex + 1}
                setValue={(update) => setPageIndex(update.num - 1)}
                min={1}
                max={numPages}
                step={1}
                showStepper={false}
                accessibilityLabel={t('Editor.inspector.document.page-title')}
                className="w-[64px] shrink-0"
              />
              <span className="text-caption-small text-sys-cont-neutral-lightest">
                / {numPages}
              </span>
              <ToolButton
                size="sm"
                state={item.pageIndex < numPages - 1 ? 'default' : 'disabled'}
                onClick={() => setPageIndex(item.pageIndex + 1)}
                aria-label={t('Editor.inspector.document.next-page')}
                isShowTooltip
                tooltipNode={t('Editor.inspector.document.next-page')}
              >
                <ChevronRight />
              </ToolButton>
            </div>

            {/* 페이지 키프레임 — 재생 중 페이지 넘김(플립 전환) 타임라인 */}
            <div className="border-t border-sys-style-neutral-light px-md py-4xs">
              <PageKeyframeControls
                itemId={item.id}
                durationInFrames={item.durationInFrames}
                numPages={numPages}
              />
            </div>
          </CollapsibleContent>
        </div>

        {/* Animation 섹션 (줌 키프레임 포함) */}
        <div className="border-b border-sys-style-neutral-light">
          <AnimationControls
            itemId={item.id}
            animations={item.animations ?? EMPTY_ANIMATIONS}
            durationInFrames={item.durationInFrames}
            collapseAllTrigger={collapseAllTrigger}
            expandAllTrigger={expandAllTrigger}
          />
        </div>
        <div className="border-b border-sys-style-neutral-light px-4xs pb-2xs">
          <AccordionHeader
            size="sm"
            title={t('Editor.inspector.timing.title')}
            isOpened={openSections.time}
            onClick={() => toggleSection('time')}
          />
          <CollapsibleContent isOpen={openSections.time}>
            <div className="px-md py-4xs">
              <TimingControls itemId={item.id} />
            </div>
          </CollapsibleContent>
        </div>
        <div className="border-b border-sys-style-neutral-light px-4xs pb-2xs">
          <AccordionHeader
            size="sm"
            title="위치 및 모양"
            isOpened={openSections.positionShape}
            onClick={() => toggleSection('positionShape')}
          />
          <CollapsibleContent isOpen={openSections.positionShape}>
            <div className="flex flex-col gap-4xs px-md py-4xs">
              {FEATURE_ALIGNMENT_CONTROL && (
                <AlignmentControls itemId={item.id} />
              )}
              {FEATURE_POSITION_CONTROL && <PositionControl itemId={item.id} />}
              {FEATURE_DIMENSIONS_CONTROL && (
                <DimensionsControls itemId={item.id} />
              )}
              {FEATURE_ROTATION_CONTROL && (
                <RotationControl
                  rotation={item.rotation}
                  itemId={item.id}
                />
              )}
              <div className={'flex w-full justify-between gap-4xs'}>
                {FEATURE_OPACITY_CONTROL && (
                  <div className={'w-full'}>
                    <OpacityControls
                      opacity={item.opacity}
                      itemId={item.id}
                    />
                  </div>
                )}
                {FEATURE_BORDER_RADIUS_CONTROL && (
                  <BorderRadiusControl
                    borderRadius={item.borderRadius}
                    itemId={item.id}
                  />
                )}
              </div>
            </div>
          </CollapsibleContent>
        </div>
      </div>
    </div>
  );
};

export const PdfInspector = React.memo(PdfInspectorUnmemoized);
